import { Component, ChangeDetectionStrategy } from '@angular/core';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faDroplet, faDropletSlash } from '@fortawesome/pro-solid-svg-icons';
import { faDropletSlash as faDropletSlashDuotone } from '@fortawesome/pro-duotone-svg-icons';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { Tooltip } from 'primeng/tooltip';

@Component({
  selector: 'app-system-attributes-cell-renderer',
  standalone: true,
  imports: [FontAwesomeModule, Tooltip],
  styles: [
    `
      .badge-families {
        background-color: var(--red-800);
        color: white;
      }
      .badge-banned {
        background-color: var(--red-100);
        color: var(--red-800);
      }
      .badge-suspended {
        background-color: var(--orange-100);
        color: var(--orange-800);
      }
      .badge-provisional {
        background-color: var(--yellow-100);
        color: var(--yellow-800);
      }
      .color-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: 1.5rem;
        height: 1.25rem;
        padding: 0 0.35rem;
        border-radius: 0.25rem;
        font-size: 0.75rem;
        font-weight: 600;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <span class="flex align-items-center gap-2 h-full">
      <!-- Pre-treatment system attributes -->
      @if (rinseProcess === 'Rinse') {
        <fa-icon [icon]="faDroplet" pTooltip="Rinse Process" tooltipPosition="top" class="cursor-help"></fa-icon>
      }
      @if (rinseProcess === 'No-Rinse') {
        <fa-icon
          [icon]="faDropletSlash"
          pTooltip="No-Rinse Process"
          tooltipPosition="top"
          class="cursor-help"></fa-icon>
      }
      @if (rinseProcess === 'Dual Use') {
        <fa-icon [icon]="faDropletSlashDuotone" pTooltip="Dual Use" tooltipPosition="top" class="cursor-help"></fa-icon>
      }
      @if (exposureYears !== null) {
        <span pTooltip="Outdoor Exposure" tooltipPosition="top" class="cursor-help"> {{ exposureYears }}y </span>
      }

      <!-- Powder system color restriction badges -->
      @if (isPowderSystem) {
        <span class="color-badge badge-families cursor-help" pTooltip="Banned Families" tooltipPosition="top">
          {{ bannedFamiliesCount > 0 ? bannedFamiliesCount : '-' }}
        </span>
        <span class="color-badge badge-banned cursor-help" pTooltip="Banned Colors" tooltipPosition="top">
          {{ bannedColorsCount > 0 ? bannedColorsCount : '-' }}
        </span>
        <span class="color-badge badge-suspended cursor-help" pTooltip="Suspended Colors" tooltipPosition="top">
          {{ suspendedColorsCount > 0 ? suspendedColorsCount : '-' }}
        </span>
        <span class="color-badge badge-provisional cursor-help" pTooltip="Provisional Colors" tooltipPosition="top">
          {{ provisionalColorsCount > 0 ? provisionalColorsCount : '-' }}
        </span>
      }

      <!-- Show dash if no attributes -->
      @if (!rinseProcess && exposureYears === null && !isPowderSystem) {
        <span>-</span>
      }
    </span>
  `,
})
export class SystemAttributesCellRendererComponent implements ICellRendererAngularComp {
  faDroplet = faDroplet;
  faDropletSlash = faDropletSlash;
  faDropletSlashDuotone = faDropletSlashDuotone;

  rinseProcess: string | null = null;
  exposureYears: number | null = null;
  isPowderSystem = false;
  bannedFamiliesCount = 0;
  bannedColorsCount = 0;
  suspendedColorsCount = 0;
  provisionalColorsCount = 0;

  agInit(params: ICellRendererParams): void {
    this.refresh(params);
  }

  refresh(params: ICellRendererParams): boolean {
    const value = params.value;
    if (value) {
      this.rinseProcess = value.rinseProcess || null;
      this.exposureYears = value.exposureYears ?? null;
      this.isPowderSystem = value.isPowderSystem || false;
      this.bannedFamiliesCount = value.bannedFamiliesCount || 0;
      this.bannedColorsCount = value.bannedColorsCount || 0;
      this.suspendedColorsCount = value.suspendedColorsCount || 0;
      this.provisionalColorsCount = value.provisionalColorsCount || 0;
    } else {
      this.rinseProcess = null;
      this.exposureYears = null;
      this.isPowderSystem = false;
      this.bannedFamiliesCount = 0;
      this.bannedColorsCount = 0;
      this.suspendedColorsCount = 0;
      this.provisionalColorsCount = 0;
    }
    return true;
  }
}
